import React, { useEffect, useCallback } from 'react';
import NoteDetail from './NoteDetail';
import { Note, NoteImpact } from '../../types/interfaces';
import './NoteModal.css';

interface NoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  note: Note | null;
  impact?: NoteImpact | null;
  isLoading?: boolean;
  error?: string | null;
  isOwner?: boolean;
  isAdmin?: boolean;
  isHearted?: boolean;
  onHeartClick?: () => void;
  onFlagClick?: () => void;
  onShareClick?: () => void;
  onEditClick?: () => void;
}

const NoteModal: React.FC<NoteModalProps> = ({
  isOpen,
  onClose,
  note,
  impact,
  isLoading = false,
  error = null,
  isOwner = false,
  isAdmin = false,
  isHearted = false,
  onHeartClick,
  onFlagClick,
  onShareClick,
  onEditClick,
}) => {
  // Handle Escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="note-modal-backdrop" onClick={handleBackdropClick}>
      <div className="note-modal-container" role="dialog" aria-modal="true" aria-label="Note details">
        <div className="note-modal-header">
          <button
            className="note-modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="note-modal-content">
          {isLoading ? (
            <div className="text-center my-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading note...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : note ? (
            <NoteDetail
              note={note}
              impact={impact || undefined}
              isOwner={isOwner}
              isAdmin={isAdmin}
              isHearted={isHearted}
              onHeartClick={onHeartClick}
              onFlagClick={onFlagClick}
              onShareClick={onShareClick}
              onEditClick={onEditClick}
            />
          ) : (
            <div className="alert alert-warning">Note not found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoteModal;
